import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setViewingOrgId } from "../api/client";
import type { CurrentUser } from "../auth/useCurrentUser";
import { useBranding } from "../context/BrandingContext";
import { ExperienceProvider, useExperience } from "../context/ExperienceContext";
import { FrameworksProvider, useFrameworks } from "../context/FrameworksContext";
import { GroupViewContext } from "../context/GroupViewContext";
import type { GroupView } from "../context/GroupViewContext";
import type { FrameworkId } from "../context/FrameworksContext";
import { onAddonsChanged } from "../nav/addonsChanged";
import { AppShell } from "../nav/AppShell";
import { CONSULTANT_HOME, clientOverviewPath } from "../nav/consultantNav";
import { buildClientNav, buildSetupChecklist, NO_ADDONS } from "../nav/clientNav";
import type { ClientAddons, DataProtectionScope } from "../nav/clientNav";
import type { NavCounts } from "../nav/NavCountsContext";
import type { NavModel, WorkspaceSwitch } from "../nav/types";
import { ClientRoutes } from "./ClientRoutes";
import { InactivityWatcher } from "./InactivityWatcher";
import { SectorPromptModal } from "./SectorPromptModal";

// ── Client layout ─────────────────────────────────────────────────────────
// The shell every client sees: AppShell (sidebar + top bar) around the client
// routes. It is the one source of truth for the client experience. Consultancy
// staff inside their own organisation's portal get exactly the same shell -
// their session *is* a client session - plus one slim strip and a user-menu
// switch that lead back to the consultant workspace.

interface ClientLayoutProps {
  user: CurrentUser;
  onAutoSignOut: () => void;
  /** Present for consultancy staff acting as their own organisation's client. */
  onBackToConsultant?: () => void;
}

// ── Group view ────────────────────────────────────────────────────────────
// A user on a group's parent organisation with group:view may look at any
// member: every API call then carries ?organizationId= (api/client.ts), the
// same mechanism as the consultant read-through, and lib/scope.ts decides
// whether the caller may read or write there. The pick is kept per tab and
// validated against the live group on every load, so a membership change by
// the consultancy cannot leave a stale organisation selected.

const GROUP_VIEW_KEY = "abacas.groupViewOrgId";

/** Non-null when this user may switch between the group's organisations. */
function groupSwitcherFor(user: CurrentUser): { canManage: boolean } | null {
  const g = user.group;
  if (!g || !user.organizationId || g.parentOrganizationId !== user.organizationId) return null;
  if (!user.permissions.includes("group:view")) return null;
  return { canManage: user.permissions.includes("group:admin") };
}

function readSavedGroupView(user: CurrentUser): string | null {
  try {
    const saved = sessionStorage.getItem(GROUP_VIEW_KEY);
    if (saved && saved !== user.organizationId && user.group?.organizationIds.includes(saved) && groupSwitcherFor(user)) return saved;
  } catch { /* storage unavailable */ }
  return null;
}

function GroupStrip({ user, viewingOrgId, orgNames, onChange }: {
  user: CurrentUser;
  viewingOrgId: string | null;
  orgNames: Map<string, string>;
  onChange: (orgId: string | null) => void;
}) {
  const switcher = groupSwitcherFor(user);
  return (
    <div style={{
      flexShrink: 0, height: 36,
      background: "var(--c-accentBg)", borderBottom: "1px solid var(--c-border)",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 16px", gap: 12,
    }}>
      <span style={{ fontSize: 11, color: "var(--c-subtle)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", background: "var(--c-panel)", color: "var(--c-accentText)", padding: "2px 8px", borderRadius: 3 }}>
          Group
        </span>
        <strong style={{ color: "var(--c-heading)" }}>{user.group?.name ?? ""}</strong>
        {switcher && viewingOrgId && (
          <span>— working in <strong style={{ color: "var(--c-heading)" }}>{orgNames.get(viewingOrgId) ?? "…"}</strong>{switcher.canManage ? "" : " (read only)"}</span>
        )}
        {!switcher && <span>— this organisation is part of the group</span>}
      </span>
      {switcher && (
        <select
          value={viewingOrgId ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          aria-label="Organisation in the group to work in"
          style={{ fontSize: 11, padding: "3px 8px", borderRadius: 3, border: "1px solid var(--c-borderStrong)", background: "var(--c-panel)", color: "var(--c-text)", maxWidth: 260 }}
        >
          <option value="">{orgNames.get(user.organizationId ?? "") ?? user.organizationName ?? "This organisation"} (parent)</option>
          {(user.group?.organizationIds ?? []).filter((id) => id !== user.organizationId).map((id) => (
            <option key={id} value={id}>{orgNames.get(id) ?? id}</option>
          ))}
        </select>
      )}
    </div>
  );
}

/** Names of the group's organisations, for the strip and switcher. */
function useGroupOrgNames(user: CurrentUser): Map<string, string> {
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const groupId = user.group?.id ?? null;
  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    api.get<{ group: { organizations?: Array<{ id: string; name: string }>; parent?: { id: string; name: string } | null } | null }>("/groups/mine")
      .then((d) => {
        if (cancelled) return;
        const next = new Map<string, string>();
        for (const o of d.group?.organizations ?? []) next.set(o.id, o.name);
        if (d.group?.parent) next.set(d.group.parent.id, d.group.parent.name);
        setNames(next);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [groupId]);
  return names;
}

function OwnOrgStrip({ orgName, onBack, persona }: { orgName: string | null; onBack: () => void; persona?: { name: string; role: string | null } }) {
  return (
    <div style={{
      flexShrink: 0, height: 36,
      background: persona ? "var(--c-accentBg)" : "var(--c-panelAlt)", borderBottom: `1px solid ${persona ? "var(--c-accent)" : "var(--c-border)"}`,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 16px", gap: 12,
    }}>
      <span style={{ fontSize: 11, color: "var(--c-subtle)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {persona ? (
          <>
            Viewing as <strong style={{ color: "var(--c-heading)" }}>{persona.name}</strong>
            {persona.role ? <> ({persona.role})</> : null}
            {orgName ? <> in {orgName}</> : null} — what you change here is saved as theirs
          </>
        ) : (
          <>
            Your company's compliance portal
            {orgName ? <> — <strong style={{ color: "var(--c-heading)" }}>{orgName}</strong></> : null}
          </>
        )}
      </span>
      <button
        onClick={onBack}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          background: "none", border: "1px solid var(--c-borderStrong)",
          borderRadius: 3, color: "var(--c-subtle)", fontSize: 11,
          padding: "4px 10px", cursor: "pointer", whiteSpace: "nowrap",
        }}
      >
        {persona ? "Return to consultant view" : "← Consultant workspace"}
      </button>
    </div>
  );
}

interface OrgShellState {
  tierLevel: number;
  addons: ClientAddons;
  /** Scope of the Data Protection page; null until the add-ons call answers. */
  dataProtection: DataProtectionScope | null;
  /** True once both requests have answered (or failed). */
  loaded: boolean;
}

/**
 * Tier level and add-on flags, fetched on shell mount (as the old Sidebar did)
 * and again whenever My Services changes an add-on.
 */
function useOrgShellState(): OrgShellState {
  const [state, setState] = useState<OrgShellState>({ tierLevel: 1, addons: NO_ADDONS, dataProtection: null, loaded: false });
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      Promise.all([
        api.get<{ addons: Partial<ClientAddons>; dataProtection?: DataProtectionScope }>("/organizations/addons").catch(() => null),
        api.get<{ organization: { serviceTier: { level: number } } }>("/organizations/profile").catch(() => null),
      ]).then(([addons, profile]) => {
        if (cancelled) return;
        setState({
          tierLevel: profile?.organization?.serviceTier?.level ?? 1,
          addons: addons ? { ...NO_ADDONS, ...addons.addons, hardening: addons.addons.hardening ?? false } : NO_ADDONS,
          dataProtection: addons?.dataProtection ?? null,
          loaded: true,
        });
      });
    };
    load();
    const unsubscribe = onAddonsChanged(load);
    return () => { cancelled = true; unsubscribe(); };
  }, []);
  return state;
}

function ClientShell({ user, onAutoSignOut, onBackToConsultant, groupView, groupOrgNames }: ClientLayoutProps & { groupView: GroupView; groupOrgNames: Map<string, string> }) {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { enrolled, pciSaqTypes } = useFrameworks();
  const { tierLevel, addons, dataProtection, loaded } = useOrgShellState();
  // Guided (the default) gives the short menu; Expert every page that applies.
  const { novice: guided } = useExperience();
  const isAdmin = user.permissions.includes("org:admin");

  // `enrolled` is a fresh Set every render; key the memo on its contents.
  const enrolledKey = Array.from(enrolled).sort().join(",");
  const saqKey = pciSaqTypes.join(",");
  const permissionsKey = user.permissions.join(",");

  const buildModel = useCallback((counts: NavCounts | null): NavModel => {
    const client = counts?.role === "client" ? counts : null;
    return {
      shellKey: "client",
      header: branding
        ? { title: branding.name, subtitle: "Compliance portal", logoUrl: branding.logoUrl }
        : { title: "Abacas", subtitle: "Security" },
      sections: buildClientNav({
        // Unknown until the profile answers: no lock affordance meanwhile.
        tierLevel: loaded ? tierLevel : null,
        addons,
        enrolled: enrolledKey ? (enrolledKey.split(",") as FrameworkId[]) : [],
        pciSaqTypes: saqKey ? saqKey.split(",") : [],
        permissions: permissionsKey ? permissionsKey.split(",") : [],
        counts: client,
        publicApiEnabled: client?.publicApiEnabled ?? false,
        frameworkScores: client?.frameworkScores ?? [],
        dataProtection,
        guided,
      }),
      setup: isAdmin ? buildSetupChecklist(client?.setup) : undefined,
    };
  }, [branding, tierLevel, loaded, addons, dataProtection, enrolledKey, saqKey, permissionsKey, isAdmin, guided]);

  const backToConsultant = useMemo(() => {
    if (!onBackToConsultant) return undefined;
    return () => {
      // Land on the consultant home before the session changes so no client
      // route flashes under the consultant shell. A persona view returns to that client.
      navigate(user.viewedBy && user.organizationId ? clientOverviewPath(user.organizationId) : CONSULTANT_HOME);
      onBackToConsultant();
    };
  }, [onBackToConsultant, navigate, user.viewedBy, user.organizationId]);

  const switches = useMemo<WorkspaceSwitch[]>(() => {
    if (!backToConsultant) return [];
    return [{
      id: "consultant",
      label: "Consultant workspace",
      description: user.viewedBy ? "Stop viewing as this person" : "Leave your company's portal",
      onSelect: backToConsultant,
    }];
  }, [backToConsultant, user.viewedBy]);

  const showGroup = !!user.group && !user.actingAsOwnOrg;
  const banner = (backToConsultant || showGroup) ? (
    <>
      {backToConsultant && (
        <OwnOrgStrip
          orgName={user.organizationName ?? null}
          onBack={backToConsultant}
          persona={user.viewedBy ? { name: user.displayName, role: user.orgRole?.name ?? null } : undefined}
        />
      )}
      {showGroup && <GroupStrip user={user} viewingOrgId={groupView.viewingOrgId} orgNames={groupOrgNames} onChange={groupView.setViewingOrg} />}
    </>
  ) : undefined;

  return (
    <>
      <InactivityWatcher onExpire={onAutoSignOut} />
      <AppShell
        model={buildModel}
        user={user}
        tierLevel={tierLevel}
        banner={banner}
        countsKey={groupView.viewingOrgId ?? undefined}
        moreToolsTo="/more-tools"
        moreToolsSection="account"
        recordSearch
        topBar={{ bell: "alerts", deadlineClock: true }}
        userMenu={{ switches, showExperienceMode: true, profileTo: "/profile", versionTo: "/whats-new" }}
      >
        <SectorPromptModal />
        <ClientRoutes />
      </AppShell>
    </>
  );
}

export function ClientLayout(props: ClientLayoutProps) {
  const { user } = props;
  // Set before the first render so nothing below fetches the parent's data by
  // mistake; cleared only when the client shell really unmounts.
  const [viewingOrgId, setViewingOrgIdState] = useState<string | null>(() => {
    const saved = readSavedGroupView(user);
    setViewingOrgId(saved);
    return saved;
  });
  // Kept in step in a layout effect, which runs before any page's data-fetching effect: a remount
  // (React StrictMode's dev double-mount, or any other) runs the cleanup, and a cleanup-only effect
  // left the strip saying "working in <subsidiary>" while every request went to the parent.
  useLayoutEffect(() => {
    setViewingOrgId(viewingOrgId);
    return () => setViewingOrgId(null);
  }, [viewingOrgId]);
  const groupOrgNames = useGroupOrgNames(user);
  const available = !!groupSwitcherFor(user) && !user.actingAsOwnOrg;

  const groupView = useMemo<GroupView>(() => ({
    viewingOrgId,
    available,
    setViewingOrg: (orgId) => {
      const next = orgId && orgId !== user.organizationId && user.group?.organizationIds.includes(orgId) && available ? orgId : null;
      setViewingOrgId(next);
      setViewingOrgIdState(next);
      try {
        if (next) sessionStorage.setItem(GROUP_VIEW_KEY, next); else sessionStorage.removeItem(GROUP_VIEW_KEY);
      } catch { /* storage unavailable */ }
    },
  }), [viewingOrgId, available, user.organizationId, user.group]);

  // Keyed on the organisation in view so frameworks, tier, add-ons and counts
  // are fetched afresh for it.
  return (
    <GroupViewContext.Provider value={groupView}>
      <ExperienceProvider>
        <FrameworksProvider key={viewingOrgId ?? "own"}>
          <ClientShell {...props} groupView={groupView} groupOrgNames={groupOrgNames} />
        </FrameworksProvider>
      </ExperienceProvider>
    </GroupViewContext.Provider>
  );
}
